import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PreviewGenerator from './PreviewGenerator';
import AuditReport from './AuditReport';

/**
 * ChatbotWidget - Assistant IA pour qualification des leads
 * Utilise Claude AI pour répondre aux questions et qualifier les prospects
 * + Mode Preview: génère un mockup de site personnalisé
 * + Mode Audit: analyse complète du site + proposition de refonte
 */
const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: "Bonjour ! Je suis l'assistant d'Olney Tech Solutions. Comment puis-je vous aider aujourd'hui ?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const messagesEndRef = useRef(null);

  // Preview mode states
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // Audit mode states
  const [isAuditMode, setIsAuditMode] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [isGeneratingAudit, setIsGeneratingAudit] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Quick replies suggestions
  const quickReplies = [
    "Auditer mon site web",
    "Voir un aperçu pour ma société",
    "Combien coûte un site ?",
    "Délai de réalisation ?"
  ];

  // API URL (backend)
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Appel à l'API chatbot avec Claude
  const getBotResponse = async (userMessage) => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory: messages.filter(m => m.id > 1) // Exclure le message de bienvenue
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur réseau');
      }

      const data = await response.json();

      if (data.success) {
        return {
          content: data.response,
          intent: data.intent || 'general',
          mode: data.mode
        };
      } else {
        throw new Error(data.message || 'Erreur API');
      }
    } catch (error) {
      console.error('Erreur chatbot:', error);
      // Fallback local en cas d'erreur
      return getFallbackResponse(userMessage);
    }
  };

  // Réponses fallback si l'API est indisponible
  const getFallbackResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    if (lowerMessage.includes('prix') || lowerMessage.includes('coût') || lowerMessage.includes('tarif') || lowerMessage.includes('combien')) {
      return {
        content: "Nos tarifs varient selon vos besoins :\n\n• **Site vitrine** : à partir de 500€ + 150€/mois\n• **E-commerce** : à partir de 3 500€\n• **Application sur-mesure** : sur devis\n\nPour un devis personnalisé, puis-je avoir votre email et une brève description de votre projet ?",
        intent: 'pricing'
      };
    }

    if (lowerMessage.includes('délai') || lowerMessage.includes('temps') || lowerMessage.includes('durée')) {
      return {
        content: "Les délais dépendent de la complexité du projet :\n\n• **Site vitrine** : 7-10 jours\n• **E-commerce** : 2-3 semaines\n• **Application complexe** : 4-8 semaines\n\nNous respectons toujours les délais convenus. Avez-vous une deadline particulière ?",
        intent: 'timeline'
      };
    }

    if (lowerMessage.includes('site') || lowerMessage.includes('web') || lowerMessage.includes('créer')) {
      return {
        content: "Excellent choix ! Nous créons des sites web modernes et performants.\n\nPour mieux vous conseiller, pouvez-vous me dire :\n1. Quel type de site ? (vitrine, e-commerce, blog...)\n2. Avez-vous déjà un nom de domaine ?\n3. Quel est votre budget approximatif ?",
        intent: 'website_creation'
      };
    }

    if (lowerMessage.includes('seo') || lowerMessage.includes('référencement') || lowerMessage.includes('google')) {
      return {
        content: "Le référencement est crucial pour être visible ! Nous proposons :\n\n• **Audit SEO** : analyse complète de votre site\n• **SEO Local** : dominer les recherches locales\n• **Suivi mensuel** : optimisation continue\n\nAvez-vous déjà un site web à optimiser ou partez-vous de zéro ?",
        intent: 'seo'
      };
    }

    if (lowerMessage.includes('contact') || lowerMessage.includes('appeler') || lowerMessage.includes('rdv') || lowerMessage.includes('rendez-vous')) {
      return {
        content: "Je vous mets en relation avec Anthony !\n\n📞 **Téléphone** : 06 89 17 73 85\n✉️ **Email** : contact@olney.dev\n\nOu laissez-moi votre email et votre numéro, nous vous rappelons sous 24h !",
        intent: 'contact'
      };
    }

    if (lowerMessage.includes('merci') || lowerMessage.includes('super') || lowerMessage.includes('parfait')) {
      return {
        content: "Avec plaisir ! N'hésitez pas si vous avez d'autres questions. Bonne journée !",
        intent: 'thanks'
      };
    }

    return {
      content: "Je comprends votre demande. Pour vous apporter la meilleure réponse, pourriez-vous me donner plus de détails ?\n\nOu si vous préférez, contactez directement Anthony au **06 89 17 73 85** - il sera ravi de vous aider !",
      intent: 'unknown'
    };
  };

  // Générer un preview pour une entreprise
  const generatePreview = async (companyName) => {
    setIsGeneratingPreview(true);

    try {
      const response = await fetch(`${API_URL}/api/company-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companyName }),
      });

      const data = await response.json();

      if (data.success && data.preview) {
        setPreviewData(data.preview);
        setIsPreviewMode(true);

        // Message de succès
        const successMessage = {
          id: messages.length + 2,
          type: 'bot',
          content: `Voici un aperçu de ce que pourrait être le site de **${companyName}** ! Cliquez sur "Obtenir ce site" si vous êtes intéressé.`,
          intent: 'preview_success',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, successMessage]);
      } else {
        throw new Error('Erreur génération preview');
      }
    } catch (error) {
      console.error('Erreur preview:', error);
      const errorMessage = {
        id: messages.length + 2,
        type: 'bot',
        content: "Désolé, je n'ai pas pu générer l'aperçu. Pouvez-vous réessayer avec le nom exact de votre entreprise ?",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsGeneratingPreview(false);
      setIsTyping(false);
    }
  };

  // Détecter si l'utilisateur demande un preview
  const detectPreviewIntent = (message) => {
    const lowerMessage = message.toLowerCase();
    const previewKeywords = ['aperçu', 'preview', 'voir mon site', 'visualiser', 'mockup', 'exemple pour'];

    // Si le message contient un mot-clé de preview
    if (previewKeywords.some(kw => lowerMessage.includes(kw))) {
      return true;
    }

    // Si on attend un nom de société (après avoir demandé)
    if (messages.length > 0) {
      const lastBotMessage = [...messages].reverse().find(m => m.type === 'bot');
      if (lastBotMessage && lastBotMessage.intent === 'asking_company_name') {
        return true;
      }
    }

    return false;
  };

  // Extraire le nom de l'entreprise du message
  const extractCompanyName = (message) => {
    // Patterns pour extraire le nom de société
    const patterns = [
      /(?:pour|de|chez|société|entreprise|ma société|mon entreprise)\s+[«"']?([^»"'\n,.]+)[»"']?/i,
      /^([A-Z][a-zA-ZÀ-ÿ\s&'-]+)$/,
      /je\s+(?:suis|travaille\s+(?:pour|chez))\s+([^,.]+)/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match && match[1] && match[1].trim().length >= 2) {
        return match[1].trim();
      }
    }

    // Si le message est court et ressemble à un nom d'entreprise
    const trimmed = message.trim();
    if (trimmed.length >= 2 && trimmed.length <= 50 && !trimmed.includes(' dit ') && !trimmed.includes('?')) {
      // Vérifie si ça ressemble à un nom (pas une question ou phrase complexe)
      const wordCount = trimmed.split(/\s+/).length;
      if (wordCount <= 5) {
        return trimmed;
      }
    }

    return null;
  };

  // Générer un audit complet du site
  const generateAudit = async (input) => {
    setIsGeneratingAudit(true);
    setIsTyping(true);

    try {
      const response = await fetch(`${API_URL}/api/site-audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ input }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.mode === 'no_site') {
          // Pas de site trouvé -> proposer création
          const noSiteMessage = {
            id: messages.length + 2,
            type: 'bot',
            content: `Je n'ai pas trouvé de site web pour **${data.companyName}**.\n\nBonne nouvelle ! Nous pouvons créer votre premier site professionnel :\n\n✓ Visibilité en ligne 24/7\n✓ Crédibilité professionnelle\n✓ Génération de leads\n✓ Référencement local\n\nVoulez-vous voir un **aperçu personnalisé** de votre futur site ?`,
            intent: 'no_site_offer',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, noSiteMessage]);
        } else if (data.mode === 'full_audit') {
          // Audit complet réussi
          setAuditData(data.audit);
          setIsAuditMode(true);

          const auditMessage = {
            id: messages.length + 2,
            type: 'bot',
            content: `Audit terminé pour **${data.audit.companyName}** !\n\n📊 Score SEO: **${data.audit.scores.seo}/100**\n🔍 ${data.audit.scores.issues.length} problèmes détectés\n\nConsultez le rapport complet pour voir les recommandations et la proposition de refonte.`,
            intent: 'audit_complete',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, auditMessage]);
        }
      } else {
        throw new Error(data.message || 'Erreur audit');
      }
    } catch (error) {
      console.error('Erreur audit:', error);
      const errorMessage = {
        id: messages.length + 2,
        type: 'bot',
        content: "Désolé, je n'ai pas pu analyser ce site. Vérifiez l'URL ou réessayez avec le nom de l'entreprise.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsGeneratingAudit(false);
      setIsTyping(false);
    }
  };

  // Extraire l'URL du message
  const extractUrl = (message) => {
    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9-]+\.(com|fr|io|net|org|eu|dev|tech|co)(\/[^\s]*)?)/gi;
    const match = message.match(urlPattern);
    return match ? match[0] : null;
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsTyping(true);

    const lowerInput = currentInput.toLowerCase();
    const lastBotMessage = [...messages].reverse().find(m => m.type === 'bot');

    // === AUDIT FLOW ===
    // Cas A1: L'utilisateur demande un audit
    if (lowerInput.includes('audit') || lowerInput.includes('analyser mon site') || lowerInput.includes('tester mon site')) {
      setTimeout(() => {
        const askUrlMessage = {
          id: messages.length + 2,
          type: 'bot',
          content: "Parfait ! Pour analyser votre site, donnez-moi :\n\n**L'URL de votre site** (ex: www.monsite.fr)\n\nou\n\n**Le nom de votre entreprise** si vous n'avez pas encore de site.",
          intent: 'asking_site_url',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, askUrlMessage]);
        setIsTyping(false);
      }, 800);
      return;
    }

    // Cas A2: On attendait une URL/nom pour l'audit
    if (lastBotMessage?.intent === 'asking_site_url' && currentInput.trim().length >= 2) {
      await generateAudit(currentInput.trim());
      return;
    }

    // Cas A3: L'utilisateur donne directement une URL
    const extractedUrl = extractUrl(currentInput);
    if (extractedUrl) {
      await generateAudit(extractedUrl);
      return;
    }

    // === PREVIEW FLOW ===
    // Vérifier si c'est une demande de preview
    const isPreviewRequest = detectPreviewIntent(currentInput);
    const companyName = extractCompanyName(currentInput);

    // Cas P1: L'utilisateur demande un aperçu et fournit un nom
    if (isPreviewRequest && companyName) {
      await generatePreview(companyName);
      return;
    }

    // Cas P2: L'utilisateur demande un aperçu sans nom -> demander le nom
    if (lowerInput.includes('aperçu') || lowerInput.includes('voir un aperçu')) {
      setTimeout(() => {
        const askNameMessage = {
          id: messages.length + 2,
          type: 'bot',
          content: "Super idée ! Donnez-moi le **nom de votre entreprise** et je vous génère un aperçu personnalisé de votre futur site web !",
          intent: 'asking_company_name',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, askNameMessage]);
        setIsTyping(false);
      }, 800);
      return;
    }

    // Cas P3: On attendait un nom de société
    if (lastBotMessage?.intent === 'asking_company_name' && currentInput.trim().length >= 2) {
      await generatePreview(currentInput.trim());
      return;
    }

    // Cas P4: Après un audit sans site, l'utilisateur veut voir un aperçu
    if (lastBotMessage?.intent === 'no_site_offer' && (lowerInput.includes('oui') || lowerInput.includes('aperçu'))) {
      // Extraire le nom de la dernière mention
      const noSiteMatch = lastBotMessage.content.match(/pour \*\*([^*]+)\*\*/);
      if (noSiteMatch) {
        await generatePreview(noSiteMatch[1]);
        return;
      }
    }

    // Cas normal: appel à l'API chatbot
    setTimeout(async () => {
      const response = await getBotResponse(currentInput);

      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        content: response.content,
        intent: response.intent,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleQuickReply = (reply) => {
    setInputValue(reply);
    setTimeout(() => handleSend(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessage = (content) => {
    // Convert markdown-like syntax to HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br />');
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: 'spring' }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white shadow-lg shadow-purple-500/30 flex items-center justify-center hover:scale-110 transition-transform ${isOpen ? 'hidden' : ''}`}
        aria-label="Ouvrir le chat"
      >
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>

        {/* Notification Badge */}
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center animate-pulse">
          1
        </span>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[550px] max-h-[calc(100vh-6rem)] bg-[#0a0a1a] rounded-3xl shadow-2xl shadow-purple-500/20 border border-white/10 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-[var(--primary)]/20 to-[var(--secondary)]/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center">
                  <span className="text-xl">🤖</span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Assistant Olney</h3>
                  <p className="text-green-400 text-xs flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    En ligne
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors p-2"
                aria-label="Fermer le chat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* GDPR Consent Banner */}
            {!hasConsented && (
              <div className="p-3 bg-[var(--primary)]/10 border-b border-white/10">
                <p className="text-xs text-gray-300 mb-2">
                  En utilisant ce chat, vous acceptez que vos messages soient traités pour vous répondre.
                  <a href="/confidentialite" className="text-[var(--primary)] underline ml-1">En savoir plus</a>
                </p>
                <button
                  onClick={() => setHasConsented(true)}
                  className="text-xs bg-[var(--primary)] text-white px-3 py-1 rounded-full hover:bg-[var(--primary)]/80 transition-colors"
                >
                  J'accepte
                </button>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white rounded-br-md'
                        : 'bg-white/10 text-gray-200 rounded-bl-md'
                    }`}
                  >
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }} />
                    <div className={`text-xs mt-1 ${message.type === 'user' ? 'text-white/60' : 'text-gray-500'}`}>
                      {message.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white/10 p-3 rounded-2xl rounded-bl-md">
                    {isGeneratingAudit ? (
                      <div className="flex items-center gap-2 text-sm text-gray-300">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Audit en cours... (10-15 sec)
                      </div>
                    ) : isGeneratingPreview ? (
                      <div className="flex items-center gap-2 text-sm text-gray-300">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Génération de l'aperçu...
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            {messages.length <= 2 && (
              <div className="px-4 pb-2">
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      className="text-xs px-3 py-1.5 rounded-full border border-[var(--primary)]/50 text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white transition-colors"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Écrivez votre message..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[var(--primary)] focus:outline-none transition-colors placeholder-gray-500"
                  disabled={!hasConsented}
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || !hasConsented}
                  className="px-4 py-3 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {isPreviewMode && previewData && (
          <PreviewGenerator
            preview={previewData}
            onClose={() => {
              setIsPreviewMode(false);
            }}
            onContact={() => {
              setIsPreviewMode(false);
              // Ajouter un message de contact
              const contactMessage = {
                id: messages.length + 1,
                type: 'bot',
                content: `Excellent choix ! Pour créer le site de **${previewData.companyName}**, laissez-moi vos coordonnées :\n\n📧 **Email** :\n📞 **Téléphone** :\n\nOu appelez directement Anthony au **06 89 17 73 85** pour un devis gratuit !`,
                intent: 'contact',
                timestamp: new Date()
              };
              setMessages(prev => [...prev, contactMessage]);
            }}
          />
        )}
      </AnimatePresence>

      {/* Audit Report Modal */}
      <AnimatePresence>
        {isAuditMode && auditData && (
          <AuditReport
            audit={auditData}
            onClose={() => {
              setIsAuditMode(false);
            }}
            onViewRedesign={() => {
              setIsAuditMode(false);
              // Utiliser le redesignPreview de l'audit pour afficher le mockup
              if (auditData.redesignPreview) {
                setPreviewData(auditData.redesignPreview);
                setIsPreviewMode(true);
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;
